import {
  decimal,
  nullableDecimal,
} from '@snap-types/schemas/emptyish-coercions';
import { z } from 'zod/v4';

import { normalizeConsultEngineeringValue } from './quote-line';

export const alphaQuotePriceImportSchema = z.object({
  quantity: decimal,

  sems1Cost: nullableDecimal,
  sems2Cost: nullableDecimal,
  osp2Price: nullableDecimal,
  osp3Price: nullableDecimal,

  totalPreTrimWeight: decimal,
  totalFinalWeight: decimal,

  headingRunHours: decimal,
  headingCost: decimal,
  pinchRunHours: nullableDecimal,
  pinchCost: nullableDecimal,
  rollingRunHours: nullableDecimal,
  rollingCost: nullableDecimal,
  trimmingRunHours: nullableDecimal,
  trimmingCost: nullableDecimal,
  shankSlottingRunHours: nullableDecimal,
  shankSlottingCost: nullableDecimal,
  sawSlottingRunHours: nullableDecimal,
  sawSlottingCost: nullableDecimal,
  dephosCost: nullableDecimal,
  heatTreatCost: nullableDecimal,
  materialCost: decimal,
  sems1TotalCost: decimal,
  sems2TotalCost: decimal,

  serrationsCost: nullableDecimal,
  surfaceFinishCost: decimal,
  osp2Cost: nullableDecimal,
  osp3Cost: nullableDecimal,
  toolingCost: decimal,

  totalCost: decimal,

  unitCost: decimal,
  grossMargin: decimal,
  unitPrice: decimal,
  revenue: decimal,
  finalGrossMargin: decimal,
});

export type AlphaQuotePriceImport = z.output<
  typeof alphaQuotePriceImportSchema
>;

const strictPriceShape = alphaQuotePriceImportSchema.shape;

function preprocessConsultField<TSchema extends z.ZodTypeAny>(schema: TSchema) {
  return z.preprocess(normalizeConsultEngineeringValue, schema);
}

const consultEngineeringPriceShape = {
  quantity: preprocessConsultField(strictPriceShape.quantity),

  sems1Cost: preprocessConsultField(strictPriceShape.sems1Cost),
  sems2Cost: preprocessConsultField(strictPriceShape.sems2Cost),
  osp2Price: preprocessConsultField(strictPriceShape.osp2Price),
  osp3Price: preprocessConsultField(strictPriceShape.osp3Price),

  totalPreTrimWeight: preprocessConsultField(nullableDecimal),
  totalFinalWeight: preprocessConsultField(nullableDecimal),

  headingRunHours: preprocessConsultField(nullableDecimal),
  headingCost: preprocessConsultField(nullableDecimal),
  pinchRunHours: preprocessConsultField(strictPriceShape.pinchRunHours),
  pinchCost: preprocessConsultField(strictPriceShape.pinchCost),
  rollingRunHours: preprocessConsultField(nullableDecimal),
  rollingCost: preprocessConsultField(nullableDecimal),
  trimmingRunHours: preprocessConsultField(strictPriceShape.trimmingRunHours),
  trimmingCost: preprocessConsultField(strictPriceShape.trimmingCost),
  shankSlottingRunHours: preprocessConsultField(
    strictPriceShape.shankSlottingRunHours,
  ),
  shankSlottingCost: preprocessConsultField(strictPriceShape.shankSlottingCost),
  sawSlottingRunHours: preprocessConsultField(
    strictPriceShape.sawSlottingRunHours,
  ),
  sawSlottingCost: preprocessConsultField(strictPriceShape.sawSlottingCost),
  dephosCost: preprocessConsultField(strictPriceShape.dephosCost),
  heatTreatCost: preprocessConsultField(strictPriceShape.heatTreatCost),
  materialCost: preprocessConsultField(nullableDecimal),
  sems1TotalCost: preprocessConsultField(nullableDecimal),
  sems2TotalCost: preprocessConsultField(nullableDecimal),

  serrationsCost: preprocessConsultField(strictPriceShape.serrationsCost),
  surfaceFinishCost: preprocessConsultField(nullableDecimal),
  osp2Cost: preprocessConsultField(strictPriceShape.osp2Cost),
  osp3Cost: preprocessConsultField(strictPriceShape.osp3Cost),
  toolingCost: preprocessConsultField(nullableDecimal),

  totalCost: preprocessConsultField(nullableDecimal),

  unitCost: preprocessConsultField(nullableDecimal),
  grossMargin: preprocessConsultField(nullableDecimal),
  unitPrice: preprocessConsultField(nullableDecimal),
  revenue: preprocessConsultField(nullableDecimal),
  finalGrossMargin: preprocessConsultField(nullableDecimal),
} satisfies z.ZodRawShape;

export const alphaQuotePriceConsultEngineeringImportSchema = z.object(
  consultEngineeringPriceShape,
);

export type AlphaQuotePriceConsultEngineeringImport = z.output<
  typeof alphaQuotePriceConsultEngineeringImportSchema
>;
